import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useAuth } from '@/lib/authContext'
import {
  useGetMyBookmarkedRecipeIdsLazyQuery,
  useAddBookmarkMutation,
  useRemoveBookmarkMutation,
} from 'graphql/generated/graphql'

export const useBookmarkedRecipeIds = () => {
  const { isAuthenticated, checkAuth } = useAuth()
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<number>>(new Set())

  const [loadBookmarkedIds] = useGetMyBookmarkedRecipeIdsLazyQuery({
    fetchPolicy: 'no-cache',
  })
  const [addBookmark] = useAddBookmarkMutation()
  const [removeBookmark] = useRemoveBookmarkMutation()

  const refetchBookmarkedIds = useCallback(async () => {
    if (!isAuthenticated) {
      setBookmarkedIds(new Set())
      return
    }
    const result = await loadBookmarkedIds()
    setBookmarkedIds(new Set(result?.data?.myBookmarkedRecipeIds || []))
  }, [isAuthenticated, loadBookmarkedIds])

  useEffect(() => {
    refetchBookmarkedIds()
  }, [refetchBookmarkedIds])

  const toggleBookmark = useCallback(
    async (recipeId: number) => {
      if (!checkAuth()) return

      const wasBookmarked = bookmarkedIds.has(recipeId)

      setBookmarkedIds(prev => {
        const next = new Set(prev)
        if (wasBookmarked) {
          next.delete(recipeId)
        } else {
          next.add(recipeId)
        }
        return next
      })

      try {
        if (wasBookmarked) {
          await removeBookmark({ variables: { recipe_id: recipeId } })
        } else {
          await addBookmark({ variables: { recipe_id: recipeId } })
        }
      } catch (error) {
        setBookmarkedIds(prev => {
          const next = new Set(prev)
          if (wasBookmarked) {
            next.add(recipeId)
          } else {
            next.delete(recipeId)
          }
          return next
        })
        toast.error(wasBookmarked ? 'Failed to remove bookmark' : 'Failed to save bookmark')
      }
    },
    [bookmarkedIds, checkAuth, addBookmark, removeBookmark]
  )

  return { bookmarkedIds, toggleBookmark, refetchBookmarkedIds }
}
